"use client"

import React, { useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { api } from "../services/api"
import { FiArrowLeft, FiMail, FiLock, FiCheckCircle, FiAlertCircle } from "react-icons/fi"
import logo from "../images/logo.svg"

function ForgotPassword() {
  const [email, setEmail] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [message, setMessage] = useState({ text: "", type: "" })
  const [isLoading, setIsLoading] = useState(false)
  const [searchParams] = useSearchParams()
  const token = searchParams.get("token")
  const navigate = useNavigate()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setMessage({ text: "", type: "" })
    setIsLoading(true)

    try {
      if (token) {
        // Si un token est présent, c'est une réinitialisation de mot de passe
        if (newPassword !== confirmPassword) {
          setMessage({ text: "Les mots de passe ne correspondent pas", type: "error" })
          return
        }

        if (newPassword.length < 8) {
          setMessage({
            text: "Le mot de passe doit contenir au moins 8 caractères",
            type: "error",
          })
          return
        }

        await api.resetPassword({
          token,
          newPassword,
          confirmPassword,
        })

        setMessage({
          text: "Votre mot de passe a été réinitialisé avec succès. Redirection vers la page de connexion...",
          type: "success",
        })

        // Rediriger vers la page de connexion après 3 secondes
        setTimeout(() => {
          navigate("/login")
        }, 3000)
      } else {
        // Sinon, c'est une demande de réinitialisation
        await api.forgotPassword({ email })
        setMessage({
          text: "Si votre adresse email est enregistrée, vous recevrez un email de réinitialisation",
          type: "success",
        })
      }
    } catch (error) {
      setMessage({
        text: error.message || "Une erreur est survenue. Veuillez réessayer.",
        type: "error",
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyToken = async () => {
    try {
      setIsLoading(true)
      await api.verifyResetToken(token)
      // Le jeton est valide, le formulaire de réinitialisation s'affiche déjà
    } catch (error) {
      setMessage({
        text: "Le lien de réinitialisation est invalide ou a expiré",
        type: "error",
      })
    } finally {
      setIsLoading(false)
    }
  }

  // Si un token est présent, vérifier sa validité au chargement
  React.useEffect(() => {
    if (token) {
      handleVerifyToken()
    }
  }, [token])

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Link to="/">
            <img
              className="h-16 w-auto filter brightness-0 invert sepia-100 saturate-200 hue-rotate-45 drop-shadow-[0_0_10px_rgba(251,191,36,0.5)]"
              src={logo || "/placeholder.svg"}
              alt="Logo de l'application"
            />
          </Link>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
          {token ? "Réinitialisation du mot de passe" : "Mot de passe oublié ?"}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-300">
          {token
            ? "Entrez et confirmez votre nouveau mot de passe ci-dessous."
            : "Pas de problème. Entrez votre email pour recevoir un lien de réinitialisation."}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-gray-800/50 backdrop-blur-sm py-8 px-4 shadow-2xl rounded-2xl sm:px-10 border border-gray-700/50">
          {message.text && (
            <div
              className={`mb-6 p-4 rounded-lg flex items-start ${
                message.type === "error"
                  ? "bg-red-900/30 text-red-300 border-l-4 border-red-500"
                  : "bg-green-900/30 text-green-300 border-l-4 border-green-500"
              }`}
            >
              {message.type === "error" ? (
                <FiAlertCircle className="h-5 w-5 mr-3 flex-shrink-0 mt-0.5" />
              ) : (
                <FiCheckCircle className="h-5 w-5 mr-3 flex-shrink-0 mt-0.5" />
              )}
              <span>{message.text}</span>
            </div>
          )}

          {!token || message.type !== "error" ? (
            <form className="space-y-6" onSubmit={handleSubmit}>
              {!token ? (
                <div className="space-y-1">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-200">
                    Adresse email
                  </label>
                  <div className="relative mt-1 rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiMail className="h-5 w-5 text-amber-400" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="focus:ring-amber-500 focus:border-amber-500 block w-full pl-10 pr-3 py-3 border border-gray-600 bg-gray-700/50 text-white rounded-xl shadow-sm placeholder-gray-400 sm:text-sm backdrop-blur-sm"
                      placeholder="votre@email.com"
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-1">
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-200">
                      Nouveau mot de passe
                    </label>
                    <div className="relative mt-1 rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FiLock className="h-5 w-5 text-amber-400" />
                      </div>
                      <input
                        id="newPassword"
                        name="newPassword"
                        type="password"
                        autoComplete="new-password"
                        required
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="focus:ring-amber-500 focus:border-amber-500 block w-full pl-10 pr-3 py-3 border border-gray-600 bg-gray-700/50 text-white rounded-xl shadow-sm placeholder-gray-400 sm:text-sm backdrop-blur-sm"
                        placeholder="••••••••"
                        minLength={8}
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-400">Le mot de passe doit contenir au moins 8 caractères</p>
                  </div>

                  <div className="space-y-1">
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-200">
                      Confirmer le mot de passe
                    </label>
                    <div className="relative mt-1 rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FiLock className="h-5 w-5 text-amber-400" />
                      </div>
                      <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        autoComplete="new-password"
                        required
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="focus:ring-amber-500 focus:border-amber-500 block w-full pl-10 pr-3 py-3 border border-gray-600 bg-gray-700/50 text-white rounded-xl shadow-sm placeholder-gray-400 sm:text-sm backdrop-blur-sm"
                        placeholder="••••••••"
                        minLength={8}
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-black bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-500 hover:to-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 focus:ring-offset-gray-800 disabled:opacity-70 disabled:cursor-not-allowed transition duration-150 ease-in-out transform hover:-translate-y-0.5 hover:shadow-lg hover:shadow-amber-500/25"
                >
                  {isLoading ? (
                    <span className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-black"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Traitement...
                    </span>
                  ) : token ? (
                    "Réinitialiser le mot de passe"
                  ) : (
                    "Envoyer le lien de réinitialisation"
                  )}
                </button>
              </div>
            </form>
          ) : null}

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-800/50 text-gray-400">Ou</span>
              </div>
            </div>

            <div className="mt-6">
              <Link
                to="/login"
                className="w-full flex items-center justify-center py-2.5 px-4 border border-gray-600 rounded-xl shadow-sm text-sm font-medium text-gray-200 bg-gray-700/50 hover:bg-gray-600/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 focus:ring-offset-gray-800 transition duration-150 ease-in-out backdrop-blur-sm"
              >
                <FiArrowLeft className="h-4 w-4 mr-2" />
                Retour à la connexion
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center text-sm text-gray-400">
          <p>
            Besoin d'aide ?{" "}
            <a href="mailto:support@votresite.com" className="font-medium text-amber-400 hover:text-amber-300">
              Contactez-nous
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}

export default ForgotPassword
